import React, { useState } from 'react';
import { TrendingUp, Clock } from 'lucide-react';
import { CurrencyInput } from './CurrencyInput';
import { RangeInput } from './RangeInput';
import { ErrorMessage } from './ErrorMessage';
import type { CalculatorInputs } from './types';

interface CalculatorFormProps {
  inputs: CalculatorInputs;
  setInputs: React.Dispatch<React.SetStateAction<CalculatorInputs>>;
  error: string | null;
  isCalculating: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);

const parseCurrency = (value: string): number => {
  const cleanValue = value.replace(/[$,\s]/g, '');
  const number = parseFloat(cleanValue);
  return isNaN(number) ? 0 : number;
};

export function CalculatorForm({
  inputs,
  setInputs,
  error,
  isCalculating,
  onSubmit
}: CalculatorFormProps) {
  const [inputStates, setInputStates] = useState<Record<string, { touched: boolean; value: string }>>({
    currentReserveFund: { touched: false, value: '' },
    annualDuesAndFees: { touched: false, value: '' },
    annualExpenses: { touched: false, value: '' }
  });
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'range') {
      setInputs(prev => ({
        ...prev,
        [name]: parseFloat(value)
      }));
    } else {
      setInputStates(prev => ({
        ...prev,
        [name]: { touched: true, value }
      }));
      
      const numericValue = parseCurrency(value);
      setInputs(prev => ({
        ...prev,
        [name]: numericValue
      }));
    }
  };

  const handleFocus = (name: string) => {
    setFocusedField(name);
    
    if (!inputStates[name]?.touched) {
      setInputStates(prev => ({
        ...prev,
        [name]: { touched: true, value: '' }
      }));
    }
  };

  const handleBlur = (name: string) => {
    setFocusedField(null);
    const value = inputs[name as keyof CalculatorInputs];
    
    if (value > 0) {
      setInputStates(prev => ({
        ...prev,
        [name]: { 
          ...prev[name],
          value: formatCurrency(value)
        }
      }));
    }
  };

  const getInputValue = (name: string) => {
    if (focusedField === name) {
      return inputStates[name].value;
    }
    
    if (inputStates[name].touched) {
      return inputStates[name].value || '';
    }
    
    return formatCurrency(inputs[name as keyof CalculatorInputs]);
  };

  return (
    <div className="p-8">
      <p className="text-lg text-gray-600 mb-8 text-center">
        Enter your Association's financial details below to see the impact of investment on your reserves.
      </p>

      <form onSubmit={onSubmit} className="space-y-8">
        <div className="space-y-6">
          <CurrencyInput
            label="Current Reserve Assets"
            name="currentReserveFund"
            value={getInputValue('currentReserveFund')}
            onChange={handleInputChange}
            onFocus={() => handleFocus('currentReserveFund')}
            onBlur={() => handleBlur('currentReserveFund')}
            isFocused={focusedField === 'currentReserveFund'}
            disabled={isCalculating}
          />

          <CurrencyInput
            label="Annual Reserve Contribution"
            name="annualDuesAndFees"
            value={getInputValue('annualDuesAndFees')}
            onChange={handleInputChange}
            onFocus={() => handleFocus('annualDuesAndFees')}
            onBlur={() => handleBlur('annualDuesAndFees')}
            isFocused={focusedField === 'annualDuesAndFees'}
            disabled={isCalculating}
          />

          <CurrencyInput
            label="Annual Reserve Expenses (5 year average)"
            name="annualExpenses"
            value={getInputValue('annualExpenses')}
            onChange={handleInputChange}
            onFocus={() => handleFocus('annualExpenses')}
            onBlur={() => handleBlur('annualExpenses')}
            isFocused={focusedField === 'annualExpenses'}
            disabled={isCalculating}
          />

          <RangeInput
            label="Current Reserves - Blended Interest Rate"
            name="recentInvestmentReturns"
            value={inputs.recentInvestmentReturns}
            onChange={handleInputChange}
            min={0}
            max={10}
            step={0.1}
            icon={TrendingUp}
            unit="%"
            markers={['0%', '5%', '10%']}
            disabled={isCalculating}
          />

          <RangeInput
            label="Projection Time Range"
            name="timeRangeYears"
            value={inputs.timeRangeYears}
            onChange={handleInputChange}
            min={5}
            max={30}
            step={5}
            icon={Clock}
            unit=" Years"
            markers={['5y', '17.5y', '30y']}
            disabled={isCalculating}
          />
        </div>

        <ErrorMessage message={error} />

        <button
          type="submit"
          disabled={isCalculating}
          className="w-full flex justify-center items-center gap-2 py-4 px-6 border border-transparent rounded-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCalculating ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Calculating...
            </>
          ) : (
            'Calculate Investment Scenarios'
          )}
        </button>
      </form>
    </div>
  );
}